/**
 * The one Send link confirmation, for every surface that sends a bucket's
 * suggestion link.
 *
 * THREE pages send the daily link by hand and they all mount this:
 * `BucketStories` (the bucket page Adi pointed at), `BucketManager` (the older
 * board) and `BucketSidebar` (the Stories screen). Before this component they
 * each had their own send handler, and only one of them had a confirmation at
 * all.
 *
 * It is one component rather than three copies for the reason CLAUDE.md
 * Gotcha 25 states: remove the possibility of divergence, then add the drift
 * test. A second copy is how the pre-send purge came to run for half the
 * clients, and how one of two email senders came to render a template with no
 * stories in it for six days. A channel the WhatsApp rollout adds later must
 * not reach one page and miss two.
 *
 * It owns the whole interaction: loading the recipients, the channel choice,
 * freezing the snapshot, the send, and the sentence that describes what
 * happened. The caller supplies a bucket id and a way to show a message.
 */

import React, { useCallback, useEffect, useState } from 'react';
import { api } from '../../app/http-common';
import AppModal from '../AppModal/AppModal';
import './SendSuggestionsModal.css';

/**
 * Why WhatsApp is not on offer, in the admin's own terms.
 *
 * Each reason is a DIFFERENT action, and an unexplained missing option sends
 * people to the wrong one: turn the feature on, paste a ContentSid, wait for
 * Meta, or record an opt-in. Only `globally_disabled` can reach here from the
 * gate for a manual send, since a manual send skips the allowlist and the
 * per-bucket channel mode.
 */
export function whatsappUnavailableReason(wa) {
  if (wa?.reason === 'globally_disabled') {
    return 'WhatsApp sending is switched off for the whole system.';
  }
  if (wa?.reason === 'no_template_for_language') {
    return `There is no approved WhatsApp template for ${wa.language || "this bucket's language"} yet. `
      + 'Meta approves one per language and it takes up to two days.';
  }
  if (wa?.reason === 'no_content_sid') {
    // The template is approved; nobody pasted its id in. A different fix from
    // waiting on Meta, and a much faster one.
    return `The ${wa.language || 'bucket'} WhatsApp template has no ContentSid set in `
      + 'Admin → Configuration, so nothing can be sent on WhatsApp.';
  }
  return 'WhatsApp is not available for this bucket.';
}

export default function SendSuggestionsModal({
  bucketId,
  isOpen,
  onClose,
  /** (message, type) => void. 'info' is used for a dry run, never 'success'. */
  onResult,
  /** Called after a successful send so the caller can refresh its own view. */
  onSent,
}) {
  const [info, setInfo] = useState(null);
  const [channels, setChannels] = useState({ email: true, whatsapp: false });
  const [sending, setSending] = useState(false);

  // Loaded when the dialog OPENS, not when the page does: consent and the
  // global switch can change between the page loading and somebody pressing
  // send, and the whole point of this dialog is that it states what will
  // actually happen now.
  useEffect(() => {
    if (!isOpen || !bucketId) return undefined;
    let cancelled = false;
    setInfo(null);
    (async () => {
      try {
        const res = await api.getBucketRecipients(bucketId, { silent: true });
        if (cancelled) return;
        const emails = Array.isArray(res?.recipients) ? res.recipients : [];
        const wa = res?.whatsapp || null;
        setInfo({ emails, whatsapp: wa });
        // WhatsApp starts unticked, so pressing Send without reading sends
        // what this button has always sent. The exception is a bucket with no
        // email recipient at all, where leaving the only available channel
        // unticked would just disable the button for no reason.
        setChannels({ email: emails.length > 0, whatsapp: emails.length === 0 && Boolean(wa?.available) });
      } catch {
        if (!cancelled) setInfo({ emails: [], whatsapp: null, failed: true });
      }
    })();
    return () => { cancelled = true; };
  }, [isOpen, bucketId]);

  const chosen = [
    ...(channels.email ? ['email'] : []),
    ...(channels.whatsapp ? ['whatsapp'] : []),
  ];

  const confirm = useCallback(async () => {
    setSending(true);
    try {
      // autoShare false: sendSuggestions performs the draft to shared
      // transition itself, and a list already shared is rejected.
      const created = await api.createSuggestionList(bucketId, false, 'send_link');
      const listId = created?.list?._id;
      if (!listId) {
        onResult?.('There are no suggestions to send.', 'info');
        onClose?.();
        return;
      }
      const res = await api.sendBucketSuggestions(bucketId, listId, { silent: true }, chosen);
      const named = (info?.emails || []).map((r) => r.value).join(', ');
      const sentTo = res?.recipient || named || 'the configured recipient';
      // Three outcomes, three sentences. A dry run built and recorded the
      // exact payload and called nothing, and a held message is queued behind
      // the 08:00 rule. Reporting either as a plain send is the same lie as
      // the `wa.me` placeholder this path replaced: the operator would believe
      // a client had been messaged, and on the held path would press send
      // again and park a second copy.
      const after = res?.sendAfter
        ? new Date(res.sendAfter).toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' })
        : null;
      let message;
      let kind;
      if (res?.dryRun) {
        message = `Recorded a WhatsApp DRY RUN for ${sentTo}. Nothing was actually sent.`;
        kind = 'info';
      } else if (res?.held) {
        message = after
          ? `WhatsApp is held until ${after} and will go out then. ${sentTo}`
          : 'The WhatsApp message is held until the earliest send time and will go out then.';
        kind = 'info';
      } else {
        message = `Link sent to ${sentTo}.`;
        kind = 'success';
      }
      onResult?.(message, kind);
      onSent?.();
      onClose?.();
    } catch (err) {
      onResult?.(err?.response?.data?.error || 'The link could not be sent.', 'error');
      onClose?.();
    } finally {
      setSending(false);
    }
  }, [bucketId, chosen, info, onClose, onResult, onSent]);

  const emails = info?.emails || [];
  const wa = info?.whatsapp || null;

  return (
    <AppModal
      isOpen={isOpen}
      title="Send link"
      onCancel={onClose}
      onConfirm={confirm}
      confirmLabel="Send"
      // Nothing ticked means nothing to send. Disabled rather than hidden, so
      // an operator who unticked both can see why Send stopped working.
      confirmDisabled={!info || chosen.length === 0}
      isSubmitting={sending}
      maxWidth={460}
    >
      {!info && (
        <p style={{ margin: 0, fontSize: 14, color: '#3C4A5E' }}>Checking recipients…</p>
      )}

      {/* No deliverable recipient on either channel. Caught HERE, before the
          send, rather than as a server error afterwards: BucketManager used to
          fire the send and interpret "no contact point" out of the error
          string, which meant the failure was only discoverable by causing it.
          The link preserves the shortcut that flow offered. */}
      {info && emails.length === 0 && !wa?.available && (
        <>
          <p style={{ margin: 0, fontSize: 14, color: '#3C4A5E', lineHeight: 1.5 }}>
            This bucket has no delivery endpoint that can be sent to, so there is nothing
            to send.
          </p>
          <p style={{ margin: '10px 0 0', fontSize: 13 }}>
            <a
              href={`/admin2/publisher-buckets/${bucketId}?action=edit`}
              target="_blank"
              rel="noreferrer"
              style={{ color: '#00AEEF' }}
            >
              Configure an endpoint for this bucket
            </a>
          </p>
        </>
      )}

      {/* Every recipient's exact name and address, before anything leaves.
          There is deliberately no editing here: recipients are managed in
          Bucket administration. */}
      {emails.length > 0 && (
        <label className="ssm-channel">
          <input
            type="checkbox"
            checked={channels.email}
            onChange={(e) => setChannels((p) => ({ ...p, email: e.target.checked }))}
          />
          <span>
            <strong>Email</strong>
            {emails.length === 1 ? ' · 1 recipient' : ` · ${emails.length} recipients`}
          </span>
        </label>
      )}
      {channels.email && emails.map((r) => (
        <div className="ssm-recipient" key={r._id || r.value}>
          {r.name && <div className="ssm-recipient-name">{r.name}</div>}
          <div className="ssm-recipient-addr">{r.value}</div>
        </div>
      ))}

      {wa?.available && (
        <>
          <label className="ssm-channel">
            <input
              type="checkbox"
              checked={channels.whatsapp}
              onChange={(e) => setChannels((p) => ({ ...p, whatsapp: e.target.checked }))}
            />
            <span>
              <strong>WhatsApp</strong>
              {` · ${wa.recipients.length === 1 ? '1 recipient' : `${wa.recipients.length} recipients`}`}
              {wa.language ? ` · ${wa.language} template` : ''}
            </span>
          </label>
          {channels.whatsapp && wa.recipients.map((r) => (
            <div className="ssm-recipient" key={r.value}>
              {r.name && <div className="ssm-recipient-name">{r.name}</div>}
              <div className="ssm-recipient-addr">{r.value}</div>
            </div>
          ))}
        </>
      )}

      {/* Why WhatsApp is not on offer. A missing option with no explanation
          sends people to the wrong fix. A stored phone number is not consent
          under Meta's policy, which is why the opt-in case is the common one. */}
      {info && !wa?.available && wa?.reason && (
        <div className="ssm-note">{whatsappUnavailableReason(wa)}</div>
      )}
      {info && !wa?.available && !wa?.reason && (wa?.skipped || []).length > 0 && (
        <div className="ssm-note">
          No WhatsApp opt-in is recorded for this bucket&apos;s contacts, so WhatsApp cannot
          be used. Record one on the Clients page.
        </div>
      )}

      {emails.length > 0 && (
        <p style={{ margin: '12px 0 0', fontSize: 12, color: '#3C4A5E' }}>
          To change recipients, use <strong>Configure bucket → Settings → Endpoints</strong>.
        </p>
      )}
    </AppModal>
  );
}
